<template>
  <el-container class="root">
    <el-main>
      <!-- Loading indicator -->
      <span class="data-loading-indicator" v-loading="licensesLoading" />

      <el-row type="flex" justify="space-between" v-show="licenses.length > 0">
        <div style="display: flex; flex-direction: row;">
          <el-switch
            style=""
            v-model="showExpired"
            active-text="Show expired" />
          <el-switch
            style="margin-left: 10px;"
            v-model="showAutoGenerated"
            active-text="Show auto-generated" />
        </div>
        <el-col :span="8">
          <el-input prefix-icon="el-icon-search" placeholder="Search for a license..." v-model="searchQuery" clearable />
        </el-col>
      </el-row>

      <div style="height: calc(100vh - 175px)">
        <vxe-table
            highlight-hover-row
            max-height="100%"
            ref="licenseTable"
            class="license-list"
            :row-class-name="rowClassName"
            :data="visibleLicenses"
            @cell-click="tableCellClick">
          <vxe-table-column title="Email" sortable field="email" fixed="left" min-width="100" width="auto" />
          <vxe-table-column title="Number" sortable field="number" fixed="left" min-width="100" width="auto" />
          <vxe-table-column title="Expiration" sortable field="expiration" fixed="left" min-width="100" width="auto">
            <template v-slot="{ row }">
              <div class="cell-wrapper">
                <span class="date">{{$moment(row.expiration * 1000).format('DD MM YYYY')}}</span>
                <div v-if="row.expiration * 1000 < now" class="expired-tag">EXPIRED</div>
              </div>
            </template>
          </vxe-table-column>
          <vxe-table-column title="Name" sortable field="name" fixed="left" min-width="100" width="auto" />
          <vxe-table-column title="Org" sortable field="organization" fixed="left" min-width="100" width="auto" />
          <vxe-table-column title="Country" sortable field="country" fixed="left" min-width="100" width="auto" />
          <vxe-table-column title="Feide" sortable fixed="left" min-width="100" width="auto">
            <template v-slot="{ row }">
              <i class="feide-icon" v-if="row.feideAccess.length > 0" />
            </template>
          </vxe-table-column>
          <vxe-table-column title="Type" sortable field="type" fixed="left" min-width="100" width="auto">
            <template v-slot="{ row }">
              <div class="license-type">
                <div v-if="row.type & 3"><icon-8 name="laptop-checked" width="24" height="24" color="success" /><span>Apps</span></div>
                <div v-if="row.type & 4"><i class="el-icon-document" /><span>Lab Resources</span></div>
                <div v-if="row.type & 8"><i class="el-icon-warning color-warning" /><span>App Testing</span></div>
              </div>
            </template>
          </vxe-table-column>
          <vxe-table-column title="Collections" sortable field="collections" :fixed="smallScreen ? 'left' : 'right'" min-width="120" width="auto">
            <template v-slot="{ row }">
              <div class="license-collections">
                <el-tag
                  v-for="col in row.appCollections"
                  :key="col.id"
                  type="info"
                  effect="dark"
                  disable-transitions>
                  <span>{{col.name}} <i class="el-icon-warning color-warning" v-if="(col.accessType & 8) > 0" /></span>
                </el-tag>
              </div>
            </template>
          </vxe-table-column>
          <vxe-table-column title="Max Sessions" sortable field="maxConcurrentSessions" :fixed="smallScreen ? 'none' : 'right'" width="auto" />

          <template v-slot:empty><p>No data</p></template>
        </vxe-table>
      </div>
    </el-main>
  </el-container>
</template>

<script>
import { setPageTitle } from '@/utils'
import { mapState } from 'vuex'
import Icon8 from '@/components/icons/Icon8.vue'
import fuzzysort from 'fuzzysort'

export default {
  name: 'Licenses',
  data () {
    return {
      licensesLoading: true,
      licenses: [],
      showExpired: false,
      showAutoGenerated: false,
      searchQuery: null,

      now: Date.now(),
      smallScreen: window.innerWidth < 992
    }
  },
  mounted () {
    if (!this.user) {
      return this.$router.replace('/login');
    }
    setPageTitle("Licenses");
    this.loadLicenses();
  },
  methods: {
    async loadLicenses () {
      try {
        let offset = 0;
        for (let i = 0; i < 20; i++) {
          let limit = 10;
          if (i < 3) {
            limit = 10;
          } else if (i < 8) {
            limit = 100;
          } else if (i < 12) {
            limit = 1_000;
          } else {
            limit = 3_000;
          }

          let response = await this.$http.get(process.env.VUE_APP_ROOT_API + '/x/licenses', {
            params: { limit, offset }
          });

          if (response.data.licenses.length === 0) {
            break;
          }
          this.licenses.push(...response.data.licenses);
          offset += limit;
        }
      } catch (e) {
        console.error(e);
        if (e.response && e.response.status === 403) {
          this.$message({
            message: "You don't have access to this resource. Try logging out and back in",
            duration: 0,
            type: 'error'
          });
        }
      } finally {
        this.licensesLoading = false;
      }
    },
    rowClassName ({ row }) {
      if (row.expiration * 1000 < this.now) {
        return 'expired';
      }
    },
    tableCellClick ({ row }) {
      this.$router.push({name: 'LicensePage', params: {licenseId: row.id}});
    },
  },
  computed: {
    ...mapState({
      user: state => state.user
    }),
    visibleLicenses () {
      let toShow = this.licenses;
      if (!this.showExpired) {
        const now = Date.now();
        toShow = toShow.filter(l => l.expiration > now / 1000);
      }
      if (!this.showAutoGenerated) {
        toShow = toShow.filter(l => !l.hideInOverview);
      }
      if (this.searchQuery?.trim()) {
        toShow = fuzzysort.go(this.searchQuery, toShow, {
          keys: ["email", "number", "name", "organization", "country"]
        }).map(r => r.obj);
      }

      return toShow;
    }
  },
  components: {
    Icon8,
  }
}
</script>

<style lang="scss">
.el-table tbody td {
  padding: 7px 0;
}
.el-table .cell {
  word-break: break-word;
}
.license-list .expired {
  background-color: #fef7f7 !important;
  
  &:hover {
    background-color: #fdeaea !important;
  }
}
</style>
<style scoped lang="scss">
.root {
  margin-top: 25px;

  .el-main {
    position: relative;
  }

  .cell-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    min-height: 24px;
  }

  .expired-tag {
    position: absolute;
    bottom: -2px;
    left: 75px;
    background-color: #f56565;
    color: white;
    font-size: 10px;
    font-weight: bold;
    padding: 2px 6px;
    border-radius: 8px;
    text-transform: uppercase;
    z-index: 10;
    white-space: nowrap;
  }

  .license-type {
    display: flex;
    flex-direction: column;
    gap: 5px;

    div {
      width: 100%;
      display: flex;
      justify-content: flex-start;
      
      svg, i {
        width: 24px;
        text-align: center;
        vertical-align: middle;
        margin-right: 8px;
      }
      i {
        padding-top: 3px;
      }
    }
  }

  i.el-icon-check.color-success {
    font-weight: bold;
    font-size: 24px;
  }

  i.feide-icon {
    background-image: url("../assets/feide.png");
    background-size: cover;
    width: 30px;
    height: 36px;
    display: inline-block;
  }

  .license-collections {
    max-height: 70px;
    overflow: auto;
    display: flex;
    flex-flow: wrap;
    gap: 5px;
  }

  .data-loading-indicator {
    display: block;
    position: absolute;
    top: 8px;
    left: 50%;
    width: 35px;
    height: 35px;
    transform: translateX(-50%);
  }
}
</style>
